import { FC, memo, useCallback, useEffect, useMemo } from 'react'
import CustomSelect from "components/CustomSelect";
import { useTranslate } from '@tolgee/react';
import { IUserRole } from "types/family-user-role/family-user-role";
import { useGetUserRolesQuery } from 'api/user-roles';
import { emailMatchesDomains } from 'helpers/email-domain';

interface WhoAreYouProps {
  callback: (value: Array<IUserRole>) => void;
  selected: Array<IUserRole>;
  email?: string;
  disabled?: boolean;
  // Called when the current selection is not allowed for the entered email
  onInvalidSelection?: () => void;
}

const WhoAreYou: FC<WhoAreYouProps> = memo(({
  callback,
  selected,
  email,
  disabled,
  onInvalidSelection,
}) => {
  const { t: tPublicArea } = useTranslate('public_area');

  const { data, isFetching } = useGetUserRolesQuery();

  // Only types whose email domain rule matches the entered email
  const allowedRoles = useMemo(() =>
    data?.filter(({ allowedEmailDomains }) => emailMatchesDomains(email, allowedEmailDomains)) || [], [data, email]);

  const options = useMemo(() =>
    allowedRoles.reduce((acc, { name }) => name ? [...acc, name] : acc, [] as Array<string>), [allowedRoles]);

  useEffect(() => {
    if (!data || disabled || !selected[0]) return;
    if (!allowedRoles.some(({ _id }) => _id === selected[0]._id)) {
      onInvalidSelection?.();
    }
  }, [data, allowedRoles, selected, disabled, onInvalidSelection]);

  const onSelect = useCallback((value: number | string) => {
    const targetValue = allowedRoles.find(({ name }) => name === value);

    if (targetValue) {
      callback([targetValue]);
    }
  }, [callback, allowedRoles]);

  if (isFetching) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <>
      <h1 className='text-center font-weight-bold'>
        {tPublicArea('public_area.who_are_you')}
      </h1>
      <CustomSelect
        data={options}
        callback={onSelect}
        selected={selected[0]?.name}
        disabled={disabled}
        addCustomValue={false}
      />
      <p className='text-center'>
        {tPublicArea('public_area.kyc_info')}
      </p>
    </>
  )
})

export default WhoAreYou
